import React, { Component } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  FiX, FiCheckCircle, FiAlertCircle, FiArrowRight, FiTrash2, FiZap,
} from 'react-icons/fi';
import { C } from '../chrome';

// ─── Animations ────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// ─── Layout ────────────────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 5, 12, 0.72);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9100;
  padding: 2rem;
  animation: ${fadeIn} 140ms ease-out;
`;

const Box = styled.div`
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 1.4rem;
  width: 64rem;
  max-width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 24px 48px -12px rgba(0,0,0,0.6),
    0 12px 24px -8px rgba(0,0,0,0.45),
    inset 0 1px 0 rgba(255,255,255,0.04);
  animation: ${popIn} 180ms cubic-bezier(0.4, 0, 0.2, 1);
`;

// ─── Header ────────────────────────────────────────────────────────────────
const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.6rem 2rem;
  border-bottom: 1px solid ${C.border};
  flex-shrink: 0;
`;

const HeadLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const HeadIcon = styled.div`
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 0.8rem;
  background: ${C.purpleDim};
  border: 1px solid ${C.purple}40;
  color: ${C.purple};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const HeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const Title = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${C.textPrimary};
  letter-spacing: -0.01em;
`;

const Sub = styled.div`
  font-size: 1.15rem;
  color: ${C.textMuted};
`;

const Close = styled.button`
  background: transparent;
  border: 1px solid ${C.border};
  color: ${C.textMuted};
  cursor: pointer;
  padding: 0.6rem;
  border-radius: 0.6rem;
  display: flex;
  transition: all 0.15s;
  &:hover {
    border-color: ${C.red}60;
    background: ${C.redDim};
    color: ${C.red};
  }
`;

// ─── Body ──────────────────────────────────────────────────────────────────
const Body = styled.div`
  padding: 2rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-height: 0;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const SectionLabel = styled.div`
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${C.textMuted};
`;

const Counter = styled.div`
  font-size: 1.15rem;
  font-weight: 600;
  color: ${(p) => p.over ? C.red : C.textSecondary};
  font-variant-numeric: tabular-nums;
  span { color: ${C.textDim}; font-weight: 500; }
`;

const ClearBtn = styled.button`
  background: transparent;
  border: 1px solid ${C.border};
  color: ${C.textMuted};
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 0.5rem;
  padding: 0.4rem 0.9rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.15s;
  &:hover {
    border-color: ${C.red}60;
    color: ${C.red};
    background: ${C.redDim};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

// ─── Pool selector ─────────────────────────────────────────────────────────
const PoolRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const PoolCard = styled.button`
  background: ${(p) => (p.active ? C.purpleSoft : C.surfaceAlt)};
  border: 1.5px solid ${(p) => (p.active ? C.purple : C.border)};
  color: ${(p) => (p.active ? C.textPrimary : C.textSecondary)};
  border-radius: 0.8rem;
  padding: 1.2rem 1.4rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 1rem;
  text-align: left;
  transition: all 0.15s;
  position: relative;

  &:hover {
    border-color: ${(p) => (p.active ? C.purple : C.borderLight)};
    background: ${(p) => (p.active ? C.purpleSoft : C.surfaceHi)};
  }
`;

const PoolDot = styled.div`
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  border: 2px solid ${(p) => (p.active ? C.purple : C.borderLight)};
  background: ${(p) => (p.active ? C.purple : 'transparent')};
  flex-shrink: 0;
  transition: all 0.15s;
`;

const PoolLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const PoolName = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${C.textPrimary};
`;

const PoolHint = styled.div`
  font-size: 1.1rem;
  color: ${C.textMuted};
`;

// ─── Textarea ──────────────────────────────────────────────────────────────
const TextArea = styled.textarea`
  background: ${C.surfaceAlt};
  border: 1.5px solid ${(p) => (p.invalid ? C.red : C.border)};
  border-radius: 0.8rem;
  padding: 1.2rem 1.4rem;
  font-size: 1.3rem;
  line-height: 1.55;
  color: ${C.textPrimary};
  font-family: ui-monospace, 'SF Mono', monospace;
  resize: vertical;
  min-height: 12rem;
  max-height: 24rem;
  transition: all 0.15s;
  &::placeholder { color: ${C.textDim}; }
  &:focus {
    outline: none;
    border-color: ${C.purple};
    box-shadow: 0 0 0 3px ${C.purpleDim};
  }
`;

const Hint = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.1rem;
  color: ${C.textDim};

  kbd {
    background: ${C.surfaceAlt};
    border: 1px solid ${C.border};
    border-radius: 0.4rem;
    padding: 0.15rem 0.5rem;
    font-family: ui-monospace, 'SF Mono', monospace;
    font-size: 0.95rem;
    color: ${C.textMuted};
    margin: 0 0.2rem;
  }
`;

// ─── Chips (parsed preview + per-row results) ──────────────────────────────
const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  max-height: 18rem;
  overflow-y: auto;
  padding: 0.4rem 0;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.15rem;
  font-weight: 600;
  background: ${(p) => (p.color ? `${p.color}1a` : C.surfaceAlt)};
  border: 1px solid ${(p) => (p.color ? `${p.color}40` : C.border)};
  color: ${(p) => p.color || C.textSecondary};
  border-radius: 9999px;
  padding: 0.45rem 1rem 0.45rem 1.1rem;
  font-family: ui-monospace, 'SF Mono', monospace;
  font-variant-numeric: tabular-nums;

  button {
    all: unset;
    cursor: pointer;
    padding: 0.15rem;
    border-radius: 50%;
    display: inline-flex;
    color: ${C.textMuted};
    transition: all 0.12s;
    &:hover { color: ${C.red}; background: ${C.redDim}; }
  }
`;

const InfoStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.1rem;
  color: ${C.textDim};
  background: ${C.surfaceAlt};
  border: 1px solid ${C.border};
  border-radius: 0.6rem;
  padding: 0.6rem 1rem;

  strong { color: ${C.textSecondary}; font-weight: 600; }
`;

// ─── Result view ───────────────────────────────────────────────────────────
const SummaryStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
`;

const SummaryTile = styled.div`
  background: ${C.surfaceAlt};
  border: 1px solid ${(p) => (p.color ? `${p.color}33` : C.border)};
  border-left: 3px solid ${(p) => p.color || C.border};
  border-radius: 0.7rem;
  padding: 1rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const SummaryNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${(p) => p.color || C.textPrimary};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const SummaryLabel = styled.div`
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${C.textMuted};
`;

const ResultList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 30rem;
  overflow-y: auto;
`;

const ResultItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1rem;
  background: ${C.surfaceAlt};
  border: 1px solid ${C.border};
  border-left: 3px solid ${(p) => p.color || C.border};
  border-radius: 0.6rem;
  padding: 0.8rem 1.2rem;
`;

const ResultIcon = styled.div`
  color: ${(p) => p.color || C.textMuted};
  display: flex;
  flex-shrink: 0;
`;

const ResultName = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${C.textPrimary};
  font-family: ui-monospace, 'SF Mono', monospace;
`;

const ResultReason = styled.div`
  font-size: 1.1rem;
  color: ${(p) => p.color || C.textMuted};
  text-align: right;
`;

// ─── Footer ────────────────────────────────────────────────────────────────
const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1.4rem 2rem;
  border-top: 1px solid ${C.border};
  flex-shrink: 0;
  background: ${C.surface};
`;

const FootHint = styled.div`
  font-size: 1.05rem;
  color: ${C.textDim};

  kbd {
    background: ${C.surfaceAlt};
    border: 1px solid ${C.border};
    border-radius: 0.4rem;
    padding: 0.15rem 0.5rem;
    font-family: ui-monospace, 'SF Mono', monospace;
    font-size: 0.95rem;
    color: ${C.textMuted};
    margin: 0 0.15rem;
  }
`;

const FootActions = styled.div`
  display: flex;
  gap: 0.8rem;
`;

const Btn = styled.button`
  border-radius: 0.7rem;
  padding: 0.9rem 1.6rem;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${C.border};
  background: ${C.surfaceAlt};
  color: ${C.textSecondary};
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  &:hover { color: ${C.textPrimary}; border-color: ${C.borderLight}; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const PrimaryBtn = styled(Btn)`
  background: linear-gradient(180deg, ${C.purple} 0%, ${C.purpleDim ? '#7240b8' : '#7240b8'} 100%);
  background: ${C.purple};
  color: #fff;
  border-color: ${C.purple};
  box-shadow: 0 1px 0 rgba(255,255,255,0.12) inset, 0 4px 12px ${C.purple}30;
  &:hover { background: #9a67d8; color: #fff; border-color: #9a67d8; }
  &:disabled { background: ${C.surfaceAlt}; color: ${C.textDim}; box-shadow: none; border-color: ${C.border}; }
`;

// ─── Helpers ───────────────────────────────────────────────────────────────
const MAX_APPS = 50;

const POOL_META = {
  doctor:      { label: 'Doctor pool',      hint: 'Calls routed to a free doctor' },
  tele_caller: { label: 'Tele-caller pool', hint: 'Calls routed to a free tele-caller' },
};

const RESULT_META = {
  queued:  { color: C.green,  icon: <FiCheckCircle size={16} /> },
  skipped: { color: C.yellow, icon: <FiAlertCircle size={16} /> },
  error:   { color: C.red,    icon: <FiAlertCircle size={16} /> },
};

// Parse the textarea into a clean, deduped list. Returns:
//   { unique, total, duplicates }
const parseInput = (raw) => {
  const lines = String(raw || '')
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const seen = new Set();
  const unique = [];
  let duplicates = 0;
  for (const l of lines) {
    if (seen.has(l)) {
      duplicates += 1;
    } else {
      seen.add(l);
      unique.push(l);
    }
  }

  return { unique, total: lines.length, duplicates };
};

// ─── Component ─────────────────────────────────────────────────────────────
class ManualRedialModal extends Component {
  state = {
    raw:           '',
    pool:          'doctor',     // local override of props.agentType
    submitting:    false,
    result:        null,
    error:         '',
  };

  componentDidUpdate(prev) {
    // Mounted / opened — sync local pool from prop, prefill if applicable
    if (this.props.open && !prev.open) {
      this.setState({
        raw:        this.props.prefillApplicationNo || '',
        pool:       this.props.agentType || 'doctor',
        submitting: false,
        result:     null,
        error:      '',
      });
      // Focus the textarea on next tick
      setTimeout(() => this.textareaRef && this.textareaRef.focus(), 30);
    }
    // Pool change at the dashboard level after open: don't override user's modal-local choice
  }

  textareaRef = null;

  setRaw   = (e) => this.setState({ raw: e.target.value, error: '' });
  setPool  = (pool) => this.setState({ pool });
  clearAll = () => this.setState({ raw: '', error: '', result: null });

  removeChip = (app) => {
    // Rebuild the textarea text without `app`
    const parsed = parseInput(this.state.raw);
    const next = parsed.unique.filter((a) => a !== app).join('\n');
    this.setState({ raw: next, error: '' });
  };

  onKeyDown = (e) => {
    // Cmd/Ctrl + Enter → submit
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      this.submit();
      return;
    }
    // Esc → close
    if (e.key === 'Escape') {
      e.preventDefault();
      if (this.props.onClose) this.props.onClose();
    }
  };

  submit = async () => {
    const { onSubmit } = this.props;
    const { pool } = this.state;
    const { unique } = parseInput(this.state.raw);

    if (unique.length === 0) {
      this.setState({ error: 'Enter at least one application number.' });
      return;
    }
    if (unique.length > MAX_APPS) {
      this.setState({ error: `Maximum ${MAX_APPS} application numbers per request.` });
      return;
    }

    this.setState({ submitting: true, error: '', result: null });
    const result = await onSubmit(unique, pool);
    this.setState({ submitting: false, result });
  };

  startOver = () => this.setState({ result: null, raw: '' });

  // ─── Render: input mode ──────────────────────────────────────────────────
  renderInput() {
    const { raw, pool, error } = this.state;
    const parsed = parseInput(raw);
    const over = parsed.unique.length > MAX_APPS;

    return (
      <Body>
        <Section>
          <SectionLabel>Pool</SectionLabel>
          <PoolRow>
            {(['doctor', 'tele_caller']).map((key) => {
              const m = POOL_META[key];
              const active = pool === key;
              return (
                <PoolCard
                  key={key}
                  active={active}
                  onClick={() => this.setPool(key)}
                  type="button"
                >
                  <PoolDot active={active} />
                  <PoolLabel>
                    <PoolName>{m.label}</PoolName>
                    <PoolHint>{m.hint}</PoolHint>
                  </PoolLabel>
                </PoolCard>
              );
            })}
          </PoolRow>
        </Section>

        <Section>
          <SectionHead>
            <SectionLabel>Application numbers</SectionLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
              <Counter over={over}>
                {parsed.unique.length}<span> / {MAX_APPS}</span>
              </Counter>
              <ClearBtn onClick={this.clearAll} disabled={!raw}>
                <FiTrash2 size={12} /> Clear
              </ClearBtn>
            </div>
          </SectionHead>
          <TextArea
            ref={(r) => { this.textareaRef = r; }}
            placeholder={'APP001\nAPP002\nAPP003'}
            value={raw}
            onChange={this.setRaw}
            onKeyDown={this.onKeyDown}
            invalid={over || !!error}
            spellCheck="false"
          />
          <Hint>
            <span>One per line, or comma-separated.</span>
            <span><kbd>⌘</kbd><kbd>↵</kbd> to submit</span>
          </Hint>
        </Section>

        {parsed.unique.length > 0 && (
          <Section>
            <SectionHead>
              <SectionLabel>Parsed ({parsed.unique.length})</SectionLabel>
              {parsed.duplicates > 0 && (
                <Counter>
                  <span>{parsed.duplicates} duplicate{parsed.duplicates === 1 ? '' : 's'} removed</span>
                </Counter>
              )}
            </SectionHead>
            <ChipRow>
              {parsed.unique.map((a) => (
                <Chip key={a}>
                  {a}
                  <button onClick={() => this.removeChip(a)} aria-label={`Remove ${a}`}>
                    <FiX size={12} />
                  </button>
                </Chip>
              ))}
            </ChipRow>
          </Section>
        )}

        {error && (
          <InfoStrip style={{ borderColor: `${C.red}50`, color: C.red }}>
            <FiAlertCircle size={14} /> {error}
          </InfoStrip>
        )}
      </Body>
    );
  }

  // ─── Render: result mode ─────────────────────────────────────────────────
  renderResult() {
    const { result } = this.state;
    const queued  = Number(result.queued || 0);
    const skipped = Number(result.skipped || 0);
    const errors  = Number(result.errors || 0);
    const rows    = result.results || [];

    return (
      <Body>
        <Section>
          <SectionLabel>Summary</SectionLabel>
          <SummaryStrip>
            <SummaryTile color={C.green}>
              <SummaryNumber color={C.green}>{queued}</SummaryNumber>
              <SummaryLabel>Queued</SummaryLabel>
            </SummaryTile>
            <SummaryTile color={C.yellow}>
              <SummaryNumber color={skipped > 0 ? C.yellow : C.textPrimary}>{skipped}</SummaryNumber>
              <SummaryLabel>Skipped</SummaryLabel>
            </SummaryTile>
            <SummaryTile color={C.red}>
              <SummaryNumber color={errors > 0 ? C.red : C.textPrimary}>{errors}</SummaryNumber>
              <SummaryLabel>Errors</SummaryLabel>
            </SummaryTile>
          </SummaryStrip>
        </Section>

        <Section>
          <SectionLabel>Per-case result</SectionLabel>
          <ResultList>
            {rows.map((r, i) => {
              const meta = RESULT_META[r.status] || RESULT_META.error;
              return (
                <ResultItem key={`${r.application_no}-${i}`} color={meta.color}>
                  <ResultIcon color={meta.color}>{meta.icon}</ResultIcon>
                  <ResultName>{r.application_no}</ResultName>
                  <ResultReason color={meta.color}>
                    {r.status === 'queued' ? 'Queued for re-dial' : (r.reason || r.status)}
                  </ResultReason>
                </ResultItem>
              );
            })}
          </ResultList>
        </Section>
      </Body>
    );
  }

  render() {
    const { open, onClose } = this.props;
    const { pool, submitting, result, raw } = this.state;

    if (!open) return null;

    const parsed   = parseInput(raw);
    const canSubmit = !submitting && parsed.unique.length > 0 && parsed.unique.length <= MAX_APPS;
    const poolLabel = POOL_META[pool].label;
    const subText   = result
      ? `Result · ${poolLabel}`
      : 'Cases will be queued for the next dial cycle';

    return (
      <Overlay onClick={onClose}>
        <Box onClick={(e) => e.stopPropagation()} onKeyDown={this.onKeyDown}>
          <Head>
            <HeadLeft>
              <HeadIcon><FiZap size={18} /></HeadIcon>
              <HeadText>
                <Title>Trigger manual re-dial</Title>
                <Sub>{subText}</Sub>
              </HeadText>
            </HeadLeft>
            <Close onClick={onClose} aria-label="Close"><FiX size={18} /></Close>
          </Head>

          {result ? this.renderResult() : this.renderInput()}

          <Foot>
            <FootHint>
              {result ? (
                <span>
                  Done. Closing returns you to <strong>{poolLabel}</strong>.
                </span>
              ) : (
                <span>
                  Press <kbd>esc</kbd> to close · <kbd>⌘</kbd><kbd>↵</kbd> to submit
                </span>
              )}
            </FootHint>
            <FootActions>
              <Btn onClick={onClose} disabled={submitting}>Close</Btn>
              {result ? (
                <PrimaryBtn onClick={this.startOver}>
                  Queue more <FiArrowRight size={14} />
                </PrimaryBtn>
              ) : (
                <PrimaryBtn onClick={this.submit} disabled={!canSubmit}>
                  {submitting
                    ? 'Queuing…'
                    : (parsed.unique.length > 0
                        ? `Queue ${parsed.unique.length} case${parsed.unique.length === 1 ? '' : 's'}`
                        : 'Queue for re-dial')}
                  {!submitting && <FiArrowRight size={14} />}
                </PrimaryBtn>
              )}
            </FootActions>
          </Foot>
        </Box>
      </Overlay>
    );
  }
}

export default ManualRedialModal;
